import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import { buildUserIdToNameMap, toAssignableOptions } from '../../utils/assignableUsers';

export interface PharmacyUser {
  id: string;
  email: string;
  fullName: string;
  name: string;
  // Bare PHOX user id (UserResponseDto.phoxUserId) — Auth0's `sub` without the
  // `auth0|` prefix. The only identifier that matches the logged-in user when
  // their e-mail differs between Auth0 and the pharmacy record; see
  // `useCurrentUserDisplayName`.
  phoxUserId?: string;
  role?: string;
  // Pharmacy-scoped custom role (UserResponseDto.customRole) — the field the
  // backend's ?roles= filter matches on, exposed so callers can derive
  // role-restricted subsets from one unfiltered fetch.
  customRole?: string;
}

export interface PharmacyUsersData {
  users: PharmacyUser[];
  availableUsers: { id: string; name: string }[];
  userIdToNameMap: Map<string, string>;
}

/**
 * Hook to fetch pharmacy users for dropdowns
 * Fresh for 60 seconds (PRX-10: a Hub removal reaches the pickers within a minute)
 * @param pharmacyId - Optional pharmacy ID (defaults to active pharmacy)
 * @param roles - Optional comma-separated roles to filter by (e.g. 'pharmacy_ops,onepoint,infusion_scheduler')
 */
export function usePharmacyUsers(pharmacyId?: string, roles?: string) {
  const activePharmacy = usePharmacyStore((state) => state.activePharmacy);
  const effectivePharmacyId = pharmacyId || activePharmacy?.id;

  return useQuery({
    queryKey: ['pharmacy', 'users', effectivePharmacyId, roles],
    queryFn: async (): Promise<PharmacyUsersData> => {
      if (!effectivePharmacyId) {
        throw new Error('No pharmacy ID available');
      }

      const response = await api.get(`/api/user-management/pharmacy/${effectivePharmacyId}/users`, {
        params: roles ? { roles } : undefined,
      });

      const users = response.data || [];

      // GP-1335: shared rules (utils/assignableUsers) — drop service/support
      // accounts and label every option with a display name instead of a raw
      // e-mail. Infusion schedulers stay IN here: this list also feeds the IV
      // tables, which are their work queue. Surfaces that must not offer them
      // (Billing Queue / troubleshooting pickers) call `toAssignableOptions`
      // themselves without the flag — see useBillingQueueData.
      const availableUsers = toAssignableOptions(users, { includeInfusionSchedulers: true });

      // Lookup map for translating ANY user ID format to display name.
      // This handles legacy auth0| and phoxUserId formats stored in assignedTo.
      const userIdToNameMap = buildUserIdToNameMap(users);

      return {
        users,
        availableUsers,
        userIdToNameMap
      };
    },
    enabled: !!effectivePharmacyId,
    staleTime: 60 * 1000, // PRX-10: matches the backend's 60 s Redis copy
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
}
